import { useState } from "react";
import { Link } from "react-router";
import { ShieldCheckIcon, BoltIcon, ChartBarIcon } from "@heroicons/react/24/outline";
import { AuthModal } from "@/components/auth/AuthModal";

const markets = [
  { symbol: "ES", name: "S&P 500", category: "Index Futures" },
  { symbol: "NQ", name: "NASDAQ 100", category: "Index Futures" },
  { symbol: "GC", name: "Gold", category: "Metals" },
  { symbol: "SI", name: "Silver", category: "Metals" },
  { symbol: "BTC", name: "Bitcoin", category: "Crypto" },
  { symbol: "ETH", name: "Ethereum", category: "Crypto" },
  { symbol: "SOL", name: "Solana", category: "Crypto" },
];

const features = [
  {
    icon: ShieldCheckIcon,
    title: "Multi-venue execution",
    description:
      "Trades settle via best available partners including CQG, Interactive Brokers, and decentralized exchanges.",
  },
  {
    icon: BoltIcon,
    title: "Institutional execution",
    description:
      "CQG-powered order routing delivers sub-second fills with advanced order types including OCO and smart orders.",
  },
  {
    icon: ChartBarIcon,
    title: "Automated strategies",
    description:
      "Allocate to curated strategies managed by professional quantitative traders. Monitor performance and withdraw anytime.",
  },
];

export default function Home() {
  const [showAuthModal, setShowAuthModal] = useState(false);

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Navigation */}
      <header className="fixed left-0 right-0 top-0 z-40 border-b border-white/10 bg-slate-950/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-white text-center text-lg font-black leading-9 text-slate-900 shadow-sm">
              S
            </div>
            <p className="text-sm font-bold uppercase tracking-widest text-white">
              Sovrun
            </p>
          </Link>
          <button
            onClick={() => setShowAuthModal(true)}
            className="rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-slate-900 transition hover:bg-slate-100"
          >
            Sign in
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden pb-20 pt-32 md:pb-28 md:pt-40">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-800/50 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-6xl px-6 text-center">
          <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-green-500/30 bg-green-500/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-green-400">
            <span className="h-1.5 w-1.5 rounded-full bg-green-400" />
            Open Beta
          </p>
          <h1 className="mx-auto max-w-4xl text-4xl font-bold leading-tight text-white md:text-6xl md:leading-tight">
            Trade traditional assets
            <br />
            <span className="text-slate-400">and crypto</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-400 md:text-xl">
            Trade S&P 500, NASDAQ 100, Gold, Silver, and crypto — via the best available
            execution partners.
          </p>
          <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <button
              onClick={() => setShowAuthModal(true)}
              className="rounded-xl bg-white px-8 py-3.5 text-sm font-bold text-slate-900 shadow-lg transition hover:bg-slate-100 hover:shadow-xl"
            >
              Create free account
            </button>
            <Link
              to="/strategies"
              className="rounded-xl border border-slate-700 px-8 py-3.5 text-sm font-semibold text-slate-300 transition hover:border-slate-500 hover:text-white"
            >
              Explore strategies
            </Link>
          </div>
        </div>
      </section>

      {/* Markets */}
      <section className="py-20">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-center text-2xl font-bold text-white md:text-3xl">
            Global markets, one platform
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-center text-slate-400">
            Access the most liquid traditional asset classes alongside crypto — all from a
            single account.
          </p>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {markets.map((m) => (
              <div
                key={m.symbol}
                className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6 transition hover:border-slate-700"
              >
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {m.category}
                </p>
                <p className="mt-2 text-lg font-bold text-white">{m.name}</p>
                <p className="text-sm text-slate-400">{m.symbol}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-slate-800 py-20">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-center text-2xl font-bold text-white md:text-3xl">
            Built for serious traders
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-center text-slate-400">
            Sovrun combines institutional-grade infrastructure with automated strategies
            for a seamless trading experience across asset classes.
          </p>
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {features.map((f) => (
              <div
                key={f.title}
                className="rounded-2xl border border-slate-800 bg-slate-900/30 p-6"
              >
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-slate-800">
                  <f.icon className="h-5 w-5 text-slate-300" />
                </div>
                <h3 className="text-lg font-semibold text-white">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">
                  {f.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-slate-800 py-20">
        <div className="mx-auto max-w-2xl px-6 text-center">
          <h2 className="text-2xl font-bold text-white md:text-3xl">
            Start trading in minutes
          </h2>
          <p className="mt-3 text-slate-400">
            Create an account and access institutional-grade markets in minutes.
          </p>
          <button
            onClick={() => setShowAuthModal(true)}
            className="mt-8 rounded-xl bg-white px-8 py-3.5 text-sm font-bold text-slate-900 shadow-lg transition hover:bg-slate-100 hover:shadow-xl"
          >
            Get started
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-8">
        <div className="mx-auto max-w-6xl px-6 text-center text-sm text-slate-500">
          <p>Sovrun</p>
        </div>
      </footer>

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </div>
  );
}
